<template>
  <PageLayout>
    <template slot="header">
      <div class="d-flex m-3 mx-lg-0">
        <Breadcrumb :list="breadcrumbList" />
        <b-button
          class="right"
          variant="tertiary"
          @click="openActionPlanModal"
          v-if="loading || actionPlanList.length"
        >
          {{ labels.newActionPlanButton }}
        </b-button>
        <GroupsTreeSelect class="ml-md-1" @selectItem="selectGroup" />
      </div>
      <section class="filters m-3 mx-lg-0">
        <b-form-group
          :label="labels.filters.title.label"
          class="mr-2 name-filter"
        >
          <b-input-group>
            <b-form-input
              v-model="query.title"
              class="input-gray name-form-group"
              v-on:keyup.13="refresh"
            />
            <template v-slot:append>
              <b-button
                @click="refresh"
                id="filter-apply"
                variant="primary"
                size="lg"
              >
                <i class="material-icons">search</i>
              </b-button>
            </template>
          </b-input-group>
        </b-form-group>
        <b-form-group :label="labels.filters.status.label" class="mr-2">
          <b-form-select v-model="query.status" @change="refresh">
            <option
              v-for="option in labels.filters.status.options"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </b-form-select>
        </b-form-group>
        <b-form-group :label="labels.filters.pillars.label">
          <b-form-select v-model="query.metricId" @change="refresh">
            <option
              v-for="option in labels.filters.pillars.options"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </b-form-select>
        </b-form-group>
      </section>
    </template>
    <template slot="main">
      <section v-if="actionPlanList.length" class="action-table">
        <ul>
          <li class="header">
            <div>{{ labels.table.header.name }}</div>
            <div>{{ labels.table.header.deadline }}</div>
            <div>{{ labels.table.header.group }}</div>
            <div>{{ labels.table.header.pillar }}</div>
            <div>{{ labels.table.header.initial }}</div>
            <div>{{ labels.table.header.current }}</div>
            <div>{{ labels.table.header.goal }}</div>
            <div>{{ labels.table.header.status }}</div>
            <div>{{ labels.table.header.actions }}</div>
          </li>
          <li
            class="row"
            v-for="(plan, index) in actionPlanList"
            :key="plan._id"
          >
            <div class="title">{{ plan.title }}</div>
            <div class="expire">
              {{ new Date(plan.expiresAt).toLocaleDateString() }}
            </div>
            <div class="group">{{ plan.group.name }}</div>
            <div class="metric-label">{{ plan.metric.label }}</div>
            <div class="initial">
              <span :class="getClassByScore(plan.metric.progress.initial)">
                {{ plan.metric.progress.initial }}
              </span>
            </div>
            <div class="current">
              <span
                v-if="plan.metric.progress.current"
                :class="getClassByScore(plan.metric.progress.current)"
              >
                {{ plan.metric.progress.current }}
              </span>
            </div>
            <div class="goal">
              <span :class="getClassByScore(plan.metric.progress.goal)">
                {{ plan.metric.progress.goal }}
              </span>
            </div>
            <div class="status">
              <span :class="plan.status.id">{{ plan.status.name }}</span>
            </div>
            <div class="actions">
              <i
                class="material-icons"
                :id="`create-${index}`"
                @click="openAction(plan)"
              >
                create
              </i>
              <b-tooltip
                :id="`create-${index}`"
                :target="`create-${index}`"
                custom-class="tooltip-delete"
              >
                {{ labels.tooltips.edit }}
              </b-tooltip>
              <i
                class="material-icons"
                :id="`delete-${index}`"
                @click="onRemoveEvent(plan)"
                >delete_forever</i
              >
              <b-tooltip
                :id="`create-${index}`"
                :target="`delete-${index}`"
                custom-class="tooltip-delete"
              >
                {{ labels.tooltips.delete }}
              </b-tooltip>
            </div>
          </li>
        </ul>
        <b-pagination
          v-if="
            actionPlanPagination && actionPlanPagination.total > query.perPage
          "
          v-model="query.page"
          :total-rows="actionPlanPagination.total"
          :per-page="query.perPage"
          align="center"
          @input="refresh(false)"
        >
        </b-pagination>
      </section>
      <section v-if="loading" class="loading-content">
        <loading v-if="loading" />
        <p>{{ labels.loading }}</p>
      </section>
      <b-container v-if="!actionPlanList.length && !loading">
        <div class="empty-state-box">
          <video muted loop autoplay src="home/empty-state-users.mp4" />
          <h1>{{ labels.emptyState.title }}</h1>
          <p>{{ labels.emptyState.message }}</p>
          <b-button
            class="clickable"
            variant="tertiary"
            @click="openActionPlanModal"
          >
            {{ labels.newActionPlanButton }}
          </b-button>
        </div>
      </b-container>
      <new-action-plan @close="refresh" />
      <modal-plans
        ref="component-modal-plans"
        :title="labels.modalPlans.title"
        :hideHeaderClose="true"
        :visible="true"
        v-if="!$can('use', 'actionPlan')"
        @hide="handleHide"
      />
    </template>
  </PageLayout>
</template>

<script>
import { removeActionPlan } from '@/service/actionPlan'
import PageLayout from '@/components/Home/PageLayout.vue'
import Loading from '@/components/Shared/Loading.vue'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import NewActionPlan from '@/components/ActionPlan/New.js'
import { ACTION_PLAN_EVENT_OPEN, TEMPLATES } from '@/components/ActionPlan/Enum'
import { APP_MODAL_OPEN, SIMPLE } from '@/components/Modal/Enum'
import { mapGetters } from 'vuex'
import { HIGH } from '@/helpers/messages-priority'
import ModalPlans from '@/components/Modal/ModalPlans.vue'
import GroupsTreeSelect from '@/components/Groups/GroupsTreeSelect.vue'

export default {
  components: {
    PageLayout,
    Loading,
    Breadcrumb,
    NewActionPlan,
    ModalPlans,
    GroupsTreeSelect,
  },
  async mounted() {
    this.$root.$emit('setPageTitle', { context: this, path: 'actionPlan' })
    if (!this.$can('use', 'actionPlan')) return true

    const selectedGroup = this.selectedGroup
    if (selectedGroup && selectedGroup !== this.query.groupId) {
      this.query.groupId = selectedGroup
    }

    this.loading = true
    await this.$store.dispatch('actionPlan/loadList', this.query)
    this.loading = false
  },
  data() {
    return {
      loading: undefined,
      query: {
        page: 1,
        perPage: 10,
        title: null,
        groupId: 'company',
        status: null,
        metricId: null,
      },
    }
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
      actionPlanList: 'actionPlan/list',
      actionPlanPagination: 'actionPlan/pagination',
      selectedGroup: 'user/selectedGroup',
    }),
    breadcrumbList() {
      return [
        {
          label: this.resources.general.menu.actionPlan,
        },
      ]
    },
    labels() {
      return {
        ...this.resources.page.home.actionPlan,
        buttons: this.resources.general.buttons,
      }
    },
  },
  methods: {
    openActionPlanModal() {
      this.$root.$emit(ACTION_PLAN_EVENT_OPEN, TEMPLATES.LIST_ACTION_PLANS)
    },
    openAction(actionPlan) {
      const plan = actionPlan.group
        ? {
            ...actionPlan,
            groupId: actionPlan.group._id,
            goal: actionPlan.metric.progress.goal,
          }
        : actionPlan
      this.$store.dispatch('actionPlan/openPlan', plan)
      this.$root.$emit(ACTION_PLAN_EVENT_OPEN, TEMPLATES.FORM_ACTION_PLANS)
    },
    onRemoveEvent(plan) {
      this.confirmExclusion(plan)
    },
    confirmExclusion(plan) {
      this.$root.$emit(APP_MODAL_OPEN, {
        modal: SIMPLE,
        payload: {
          title: `${this.labels.doYouWantToDeleteActionPlan} "${plan.title}"?`,
          text: this.labels.deleteWarning,
        },
        options: {
          okTitle: this.labels.buttons.yes,
          cancelTitle: this.labels.buttons.no,
          onOk: () => this.removePlan(plan),
        },
      })
    },
    removePlan({ _id, title }) {
      const message = this.labels.removeSuccessMessage.replace('#plan', title)
      removeActionPlan(_id).then(() => {
        this.refresh()
        this.$root.$emit('app:message:show', {
          priority: HIGH,
          text: message,
          status: 'success',
          countDown: 5,
        })
      })
    },
    async refresh(resetPage = true) {
      if (resetPage) {
        this.query.page = 1
        this.query.perPage = 10
      }

      this.loading = true
      await this.$store.dispatch('actionPlan/loadList', this.query)
      this.loading = false
    },
    handleHide() {
      this.$router.push({ name: 'dashboard' })
    },
    selectGroup(group) {
      this.query.groupId = group._id
      this.refresh()
    },
    getClassByScore(score) {
      switch (true) {
        case !score:
          return 'blank'
        case score >= 0 && score < 5:
          return 'low'
        case score < 7.5:
          return 'medium'
        case score <= 10:
          return 'high'
        default:
          return 'blank'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';
@import '~bootstrap/scss/_functions';
@import '~bootstrap/scss/_variables';
@import '~bootstrap/scss/mixins/breakpoints';

header {
  .d-flex {
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }
}

.empty-state-box {
  background-color: map-get($theme-colors, 'white');
  border: 1px solid #dfdcdf;
  border-radius: 8px;

  overflow-y: hidden;
  padding: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: calc(100vh - 350px);
  text-align: center;
  width: 100%;

  video {
    text-align: center;
    min-height: 130px;
    max-height: 500px;
    max-width: 100%;
  }

  h1 {
    color: map-get($theme-colors, 'gray03');
    font-size: xx-large;
    @media (max-width: 991.98px) {
      font-size: large;
    }
  }

  p {
    color: map-get($theme-colors, 'gray03');
    font-size: large;
  }
}

.list {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  height: 100%;
  list-style: none;
  padding: 0;

  li {
    min-width: 250px;
    padding: 0.7rem;
    width: 33%;

    @include media-breakpoint-down(md) {
      width: 50%;
    }

    @include media-breakpoint-down(sm) {
      width: 100%;
    }

    /deep/ .item {
      margin: 0 auto;

      @include media-breakpoint-up(lg) {
        margin: 0;
      }
    }
  }
}

.loading-content {
  text-align: center;
  width: 100%;
}

section.filters {
  background-color: map-get($theme-colors, 'white');
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  margin: 20px auto 20px;
  padding: 15px;
  border: 1px solid #dfdcdf;
  border-radius: 8px;
  .form-group {
    margin-top: 0 !important;
  }
  .dropdown {
    width: 250px;
    max-width: 250px;
  }
  .name-filter {
    flex-grow: 1;
  }
  input {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    background: white !important;
  }
  button {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    height: 50px !important;
    z-index: 0;
  }
  select {
    min-width: 275px;
    border-radius: 25px !important;
    border: none !important;
    background: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSJub25lIiBkPSJNMCAwaDI0djI0SDBWMHoiLz48cGF0aCBkPSJNNy40MSA4LjU5TDEyIDEzLjE3bDQuNTktNC41OEwxOCAxMGwtNiA2LTYtNiAxLjQxLTEuNDF6Ii8+PC9zdmc+')
      no-repeat right 0.75rem center !important;
  }
  .dropdown-toogle,
  select,
  input {
    box-shadow: 0px 2px 10px -1px #c3c4ca !important;
    color: #32132e !important;
    font-size: 14px !important;
    font-weight: 500;
    padding-left: 25px;
    height: 50px !important;
    border: 1px solid rgb(195, 196, 202) !important;
  }
  @include media-breakpoint-down(md) {
    justify-content: flex-start;
    .form-group {
      margin-right: 20px;
    }
  }
  @include media-breakpoint-down(sm) {
    .form-group {
      width: 100%;
      max-width: none;
      margin-bottom: 10px 0 !important;
    }
    .dropdown {
      width: 100%;
      max-width: none;
      margin-bottom: 0 !important;
      button {
        height: 100% !important;
      }
    }
  }
  @include media-breakpoint-down(xs) {
    .btn,
    .dropdown,
    select,
    input {
      height: 40px !important;
    }
  }
}

@mixin display-head($content) {
  &::before {
    text-align: left;
    font-weight: bold;
    display: block;
    content: $content;
  }
}

section.action-table {
  background-color: map-get($theme-colors, 'white');
  border: 1px solid #dfdcdf;
  border-radius: 8px;

  ul {
    padding: 0;
  }
  li {
    display: -ms-grid;
    display: grid;
    -ms-grid-rows: minmax(100px, 1fr);
    grid-template-rows: minmax(100px, 1fr);
    -ms-grid-columns: 2fr 10px 1fr 10px 1.5fr 10px 1.2fr 10px 0.8fr 10px 0.8fr
      10px 0.8fr 10px 1.2fr 10px 0.8fr;
    grid-template-columns: 2fr 1fr 1.5fr 1.2fr 0.8fr 0.8fr 0.8fr 1.2fr 0.8fr;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    -webkit-box-pack: justify;
    -ms-flex-pack: justify;
    justify-content: space-between;
    list-style-type: none;
    margin: 8px 0;
    gap: 10px;
    padding: 0 15px;
    text-align: left;
    -webkit-transition: 0.5s;
    -o-transition: 0.5s;
    transition: 0.5s;
    div {
      overflow: hidden;
      -o-text-overflow: ellipsis;
      text-overflow: ellipsis;
    }
    &.header {
      -ms-grid-rows: minmax(50px, 1fr);
      grid-template-rows: minmax(50px, 1fr);
      text-transform: uppercase;
      font-weight: bold;
      font-size: 14px;
      color: #707070;
      letter-spacing: 0px;
      border-bottom: 1px solid #dfdcdf;
      margin: 15px 0;
    }
    &.row {
      -ms-grid-rows: minmax(100px, 1fr);
      grid-template-rows: minmax(100px, 1fr);
      border: 1px solid #dfdcdf;
      border-radius: 8px;
      background: #f6f7fd;
      font-size: 14px;
      color: #454545;
      .title {
        font-weight: bold;
        font-size: 16px;
      }
      .initial,
      .current,
      .goal {
        span {
          background: map-get($theme-colors, 'metric-blank');
          border-radius: 20px;
          color: white;
          display: inline-block;
          height: 27px;
          width: 35px;
          padding-top: 3px;
          text-align: center;
          font-weight: bold;
          &.low {
            background: map-get($theme-colors, 'metric-low');
          }
          &.medium {
            background: #e9b50c;
          }
          &.high {
            background: #04bb60;
          }
        }
      }
      .status {
        text-align: center;
        max-width: 120px;
        span {
          width: 100%;
          display: block;
          font-size: 12px;
          font-weight: bold;
          text-transform: uppercase;
          padding: 5px 10px;
          border-radius: 20px;
          &.expired {
            background: #d6d6d6;
          }
          &.doing {
            background: #ffc814;
          }
          &.done {
            background: #0fdc4d;
          }
        }
      }
      &:nth-child(even) {
        background: #fff;
      }
      .actions {
        i {
          cursor: pointer;
          color: #ab9eaa;
          font-size: 25px;
          margin: 0 5px;
        }
      }
      &:hover {
        -webkit-box-shadow: 0px 2px 10px -1px #c3c4ca;
        box-shadow: 0px 2px 10px -1px #c3c4ca;
      }
    }
  }
  li > *:nth-child(1) {
    -ms-grid-row: 1;
    -ms-grid-column: 1;
  }
  li > *:nth-child(2) {
    -ms-grid-row: 1;
    -ms-grid-column: 3;
  }
  li > *:nth-child(3) {
    -ms-grid-row: 1;
    -ms-grid-column: 5;
  }
  li > *:nth-child(4) {
    -ms-grid-row: 1;
    -ms-grid-column: 7;
  }
  li > *:nth-child(5) {
    -ms-grid-row: 1;
    -ms-grid-column: 9;
  }
  li > *:nth-child(6) {
    -ms-grid-row: 1;
    -ms-grid-column: 11;
  }
  li > *:nth-child(7) {
    -ms-grid-row: 1;
    -ms-grid-column: 13;
  }
  li > *:nth-child(8) {
    -ms-grid-row: 1;
    -ms-grid-column: 15;
  }
  li > *:nth-child(9) {
    -ms-grid-row: 1;
    -ms-grid-column: 17;
  }
  @include media-breakpoint-down(md) {
    ul {
      margin: 16px;
    }
    li {
      -ms-grid-columns: (1fr) [3];
      grid-template-columns: repeat(3, 1fr);
      -ms-grid-rows: (minmax(80px, 1fr)) [3] !important;
      grid-template-rows: repeat(3, minmax(80px, 1fr)) !important;
      padding: 10px;
      &.header {
        display: none;
      }
      .expire {
        @include display-head('Deadline');
      }
      .group {
        @include display-head('Grupo');
      }
      .metric-label {
        @include display-head('Pilar');
      }
      .initial {
        @include display-head('Inicial');
      }
      .current {
        @include display-head('Atual');
      }
      .goal {
        @include display-head('Objetivo');
      }
      .status {
        @include display-head('Status');
      }
      .actions {
        @include display-head('Editar/Excluir');
      }
    }
    li > *:nth-child(1) {
      -ms-grid-row: 1;
      -ms-grid-column: 1;
    }
    li > *:nth-child(2) {
      -ms-grid-row: 1;
      -ms-grid-column: 2;
    }
    li > *:nth-child(3) {
      -ms-grid-row: 1;
      -ms-grid-column: 3;
    }
    li > *:nth-child(4) {
      -ms-grid-row: 2;
      -ms-grid-column: 1;
    }
    li > *:nth-child(5) {
      -ms-grid-row: 2;
      -ms-grid-column: 2;
    }
    li > *:nth-child(6) {
      -ms-grid-row: 2;
      -ms-grid-column: 3;
    }
    li > *:nth-child(7) {
      -ms-grid-row: 3;
      -ms-grid-column: 1;
    }
    li > *:nth-child(8) {
      -ms-grid-row: 3;
      -ms-grid-column: 2;
    }
    li > *:nth-child(9) {
      -ms-grid-row: 3;
      -ms-grid-column: 3;
    }
  }
}
</style>
